import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const quickbooks = defineConnector({
  identifier: "quickbooks",
  name: "QuickBooks",
  tagLine: "Small business accounting",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: () => "https://.*\\.intuit\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an Intuit app",
      items: [
        {
          kind: "markdown",
          content:
            "Go to [developer.intuit.com](https://developer.intuit.com) → **My Hub** → **App Dashboard** → **Create an app** → select **QuickBooks Online and Payments**.",
        },
        {
          kind: "markdown",
          content:
            "Under **Keys & credentials**, add the following redirect URI for both the Development and Production environments:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/quickbooks/callback",
        },
      ],
    },
    {
      title: "Choose an environment",
      items: [
        {
          kind: "select",
          label: "Environment",
          required: true,
          target: { kind: "configuration", field: "environment" },
          defaultValue: "production",
          options: [
            { value: "production", label: "Production" },
            { value: "sandbox", label: "Sandbox" },
          ],
          helperText:
            "Use Sandbox while developing against a test company. Switch to Production once your Intuit app is approved.",
        },
      ],
    },
    {
      title: "Enter OAuth credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
          helperText:
            "From Keys & credentials in the Intuit Developer dashboard. Use the keys matching the environment selected above.",
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Connect your QuickBooks company",
      items: [
        {
          kind: "input",
          label: "Company (realm) ID",
          required: true,
          target: { kind: "configuration", field: "realmId" },
          helperText:
            "In QuickBooks Online, open the gear icon → Account and settings → Billing & subscription. The Company ID is the realm ID.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "configuration", field: "environment" },
            { kind: "configuration", field: "realmId" },
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () => "https://appcenter.intuit.com/connect/oauth2",
    tokenEndpoint: () =>
      "https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => ["com.intuit.quickbooks.accounting"],
  },
  connectionTest: {
    protocol: "http",
    request: ({ environment, realmId }) => {
      const host =
        environment === "sandbox"
          ? "sandbox-quickbooks.api.intuit.com"
          : "quickbooks.api.intuit.com";
      return `https://${host}/v3/company/${realmId}/companyinfo/${realmId}`;
    },
  },
});
